<template>
  <transition name="slide">
    <v-card
      v-if="isOpen"
      class="session-history-panel"
      variant="flat"
    >
      <div class="session-history-panel__header">
        <span class="session-history-panel__title">Conversation History</span>
        <v-btn
          icon
          size="small"
          aria-label="Close session history"
          @click="$emit('close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="session-history-panel__content pa-0">
        <v-progress-linear
          v-if="isLoading"
          indeterminate
          color="primary"
          height="2"
          class="session-history-panel__loader"
        />

        <div v-if="!hasSessions && isLoading" class="session-history-panel__loading">
          <v-progress-circular
            indeterminate
            color="primary"
            size="32"
          />
        </div>

        <div v-else-if="!hasSessions" class="session-history-panel__empty">
          <v-icon size="48" color="grey-lighten-1">mdi-message-text-outline</v-icon>
          <p class="session-history-panel__empty-title">No previous sessions</p>
          <p class="session-history-panel__empty-subtitle">Start a conversation to see it here</p>
        </div>

        <v-list v-else class="session-history-panel__list pa-0">
          <AiSessionListItem
            v-for="session in sessions"
            :key="session.id"
            :session="session"
            :is-active="session.id === activeSessionId"
            @select="$emit('select:session', $event)"
            @delete="$emit('delete:session', $event)"
          />
        </v-list>
      </v-card-text>
    </v-card>
  </transition>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { IChatSession } from '@/bundles/AiAgent/types';

import AiSessionListItem from '@/bundles/AiAgent/components/AiSessionListItem.vue';

export default defineComponent({
  name: 'AiSessionHistoryPanel',

  components: {
    AiSessionListItem,
  },

  props: {
    isOpen: Boolean,
    sessions: {
      type: Array as () => IChatSession[],
      default: () => [],
    },
    activeSessionId: {
      type: Number,
      default: null,
    },
    isLoading: Boolean,
  },

  emits: ['close', 'delete:session', 'select:session'],

  computed: {
    hasSessions (): boolean {
      return this.sessions.length > 0;
    },
  },
});
</script>

<style lang="scss" scoped>
.session-history-panel {
  position: absolute;
  top: 0;
  left: 0;
  width: 280px;
  height: 100%;
  z-index: 20;
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 12px rgba(0, 0, 0, 0.1);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    padding: 0 8px 0 16px;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    flex-shrink: 0;
  }

  &__title {
    font-size: 16px;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    position: relative;
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 48px 24px;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    text-align: center;
  }

  &__empty-title {
    font-size: 14px;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    margin: 12px 0 4px;
  }

  &__empty-subtitle {
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
    margin: 0;
  }

  &__list {
    .v-list-item {
      border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));

      &:last-child {
        border-bottom: none;
      }
    }
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
